<template>
  <div>
    <v-row class="ma-0">
      <v-col
        ref="dashboardcontent"
        :cols="showReviews ? 9 : 12"
        class="dashboardcontent px-14 pt-6"
      >
        <v-row class="ma-0">
          <!-- Summary Tab -->
          <v-col
            v-if="selectedSubTab === 0"
            class="singleproductdashboard--tab--summary"
          >
            <v-row class="singleproductdashboard--summary ma-0">
              <v-col
                class="pa-0 px-2 col-md-4 col-sm-12 mb-md-3 mb-sm-3"
                cols="4"
              >
                <div class="singleproductdashboard--summary--box pa-6">
                  <r-big-number
                    :loading="fetchingDashboardsData"
                    :title="$t('dashboards.components.totalNumReviewsTitle')"
                    :value="numReviews"
                    :previous-value="0"
                  />
                  <v-divider class="my-12" />
                  <r-time-line-chart
                    :title="$t('dashboards.components.numReviewsOverTime')"
                    spline
                    :duration-gap="timeLineChartDurationGap"
                    :data="[{
                      chartType: 'column',
                      seriesData: timeLineChartSeriesData,
                    }]"
                    :height="120"
                    @click:point="onClickTimeLineChartPoint"
                  />
                  <v-divider class="my-12" />
                  <r-rating-time-line-chart
                    :title="$t('dashboards.components.averageStarRatingOverTime')"
                    :loading="fetchingDashboardsData"
                    :max-rate-value="5"
                    :data="netRatingHistorySeriesData"
                    @click:rating="onClickRating"
                  />
                </div>
              </v-col>
              <v-col
                class="pa-0 px-2 col-md-4 col-sm-12 mb-md-3 mb-sm-3"
                cols="4"
              >
                <div class="singleproductdashboard--summary--box pa-6">
                  <r-star-rating-distribution-chart
                    :title="$t('dashboards.components.starRatingDistribution')"
                    :loading="fetchingDashboardsData"
                    :data="starRatingDistribution"
                    @click:rating="onClickRating"
                  />
                  <v-divider class="my-12" />
                  <r-net-sentiment-half-donut
                    :title="$t('dashboards.components.netSentimentDistribution')"
                    :loading="fetchingDashboardsData"
                    :net-sentiment="netSentiment"
                    :num-negative="sentimentBreakdown.negative"
                    :num-neutral="sentimentBreakdown.neutral"
                    :num-positive="sentimentBreakdown.positive"
                    @click:sentiment="onClickSentiment"
                  />
                </div>
              </v-col>
              <v-col
                class="pa-0 px-2 col-md-4 col-sm-12 mb-md-3 mb-sm-3"
                cols="4"
              >
                <div class="singleproductdashboard--summary--box pa-6">
                  <r-top-sentiment-review-cards
                    :loading="fetchingDashboardsData"
                    :search="search"
                    :applied-entities="appliedEntityFilters"
                    :positive-review="topPositiveReview"
                    :negative-review="topNegativeReview"
                    @click:see-all-reviews="() => onShowReviews({ value: true })"
                  />
                  <v-divider class="my-12" />
                  <r-trends
                    :days="trendsSelectedDays"
                    :loading="fetchingDashboardsData || fetchingTrends"
                    :total-positive="trends.totalPositive"
                    :total-negative="trends.totalNegative"
                    :positive-percent-increase="trends.positivePercentIncrease"
                    :negative-percent-increase="trends.negativePercentIncrease"
                    :positive-data="trends.positiveData"
                    :negative-data="trends.negativeData"
                    :positive-entities="trends.positiveEntities"
                    :negative-entities="trends.negativeEntities"
                    @change:daterange="handleTrendsDateChange"
                    @click:entity-sentiment="onClickEntitySentiment"
                    @click:entity="onClickEntity"
                  />
                </div>
              </v-col>
            </v-row>

            <v-row class="singleproductdashboard--sites mx-0">
              <v-col class="pa-0">
                <r-sites-analyzed
                  :data="sites"
                  :loading="fetchingDashboardsData"
                  @click:hostname="onClickHostname"
                />
              </v-col>
            </v-row>

            <v-row class="singleproductdashboard--comparison-chart mx-0">
              <v-col class="pa-0">
                <r-comparison-chart
                  :loading="fetchingDashboardsData"
                  :data="comparisonChartData.data"
                  :breakdown-keys="comparisonChartData.breakdownKeys"
                  @change:series-one="onChangeComparisonSeriesOne"
                  @change:series-two="onChangeComparisonSeriesTwo"
                  @click:series="onClickComparisonChartSeries"
                />
              </v-col>
            </v-row>
          </v-col>

          <!-- Trending Tab -->
          <v-col
            v-if="selectedSubTab === 1"
          >
            <v-row
              class="singleproductdashboard--tab--trending ma-0"
            >
              <v-col
                class="pa-0"
                cols="12"
              >
                <r-component-header class="rcomponentheader--bordered">
                  {{ $t('dashboards.components.trending') }}
                </r-component-header>
              </v-col>
              <trending-data
                :trending-data="comparisonTrendingData"
                :loading="fetchingDashboardsData || fetchingDashboardsTrendingData"
                @change:period-date-range="onChangeTrendingPeriodDate"
                @click:sentiment="onClickSentiment"
                @click:entity-name="onClickEntityName"
                @click:word="onClickWord"
                @click:rating="onClickRating"
              />
            </v-row>
          </v-col>

          <!-- Entity Tab -->
          <v-col
            v-if="selectedSubTab === 2"
          >
            <v-row
              class="singleproductdashboard--tab--entity ma-0"
            >
              <v-col
                class="pa-0"
                cols="12"
              >
                <r-component-header class="rcomponentheader--bordered">
                  {{ $t('dashboards.components.wordCloud') }}
                  <template #end>
                    <div class="d-flex align-center">
                      <r-button
                        v-if="entityMapIsApplied"
                        :disabled="selectedWords.length === 0"
                        class="wordcloudheaderbutton mr-2"
                        label="Add to entity map"
                        outlined
                        @click="onClickAddToEntityMap"
                      />
                      <r-button
                        v-if="entityMapIsApplied"
                        :disabled="selectedWords.length === 0"
                        class="wordcloudheaderbutton"
                        icon="delete"
                        label="Irrelevant"
                        outlined
                        @click="onClickIgnore"
                      />

                      <r-anchored-popup
                        v-slot="{ onClose }"
                        class="wordcloudheaderbutton entitysettingspopup"
                        fab-icon
                        icon="mdi-cog"
                        label=""
                        small-button
                      >
                        <entity-settings
                          :applied-color-by-filter="appliedColorByFilter"
                          :applied-highlight-filter="appliedHighlightFilter"
                          :applied-separate-by-filter="appliedSeparateByFilter"
                          @apply="onApplyEntitySettingsFilter"
                          @close="onClose"
                        />
                      </r-anchored-popup>
                    </div>
                  </template>
                </r-component-header>
                <r-word-cloud
                  v-if="showSingleCloudWord"
                  :entities="wordCloudData"
                  :color="appliedColorByFilter"
                  :applied-color-by-filter="appliedColorByFilter"
                  :time-buckets="timeBuckets"
                  :disable-entity-map-controls="!entityMapIsApplied"
                  :loading-data="fetchingWordCloudData"
                  @change:selected-words="onChangeSelectedWords"
                  @click:edit-entity-map-group="onClickEditEntityMapGroup"
                  @click:word="onClickWord"
                />
                <r-word-cloud-grouped
                  v-else
                  :applied-color-by-filter="appliedColorByFilter"
                  :applied-highlight-filter="appliedHighlightFilter"
                  :applied-separate-by-filter="appliedSeparateByFilter"
                  :entity-map-is-applied="entityMapIsApplied"
                  :is-getting-entities="fetchingWordCloudData"
                  :time-buckets="timeBuckets"
                  :word-cloud-grouped="wordCloudGroupedData"
                  @change:selected-words="onChangeSelectedWords"
                  @click:edit-entity-map-group="onClickEditEntityMapGroup"
                  @click:word="onClickWord"
                />
                <KeyColor
                  v-if="appliedColorByFilter"
                  :applied-colors="appliedColors"
                  :reversed-keys="appliedColorByFilter === 'rating'"
                />
              </v-col>
              <v-col
                class="pa-0"
                cols="12"
              >
                <r-component-header class="rcomponentheader--bordered">
                  {{ $t('dashboards.components.entityProgress') }}
                </r-component-header>
                <entity-progress
                  :loading="fetchingDashboardsData"
                  :width="entityProgressBarChartWidth"
                  @click:entity="onClickEntity"
                />
              </v-col>
              <v-col
                class="pa-0"
                cols="12"
              >
                <r-component-header class="rcomponentheader--bordered">
                  {{ $t('dashboards.components.topicWheel') }}
                </r-component-header>
                <r-topic-wheel
                  title=""
                  :hierarchical="true"
                  :skeleton-size="370"
                  :data="topicWheelData"
                  :loading="fetchingDashboardsEntitiesWithGroupData"
                  @click:entity-name="onClickEntityName"
                />
              </v-col>
            </v-row>
          </v-col>

          <!-- Location Tab -->
          <v-col
            v-if="selectedSubTab === 3"
          >
            <v-row
              class="singleproductdashboard--tab--location ma-0"
            >
              <v-col
                class="pa-0"
                cols="12"
              >
                <r-component-header class="rcomponentheader--bordered">
                  {{ $t('dashboards.components.location') }}
                </r-component-header>
              </v-col>
              <v-col
                class="pa-0"
                cols="6"
              >
                <r-map
                  :loading="fetchingDashboardsData"
                  :data="locationsMap"
                  @click:country="onClickMapCountry"
                />
              </v-col>
              <v-col
                class="pa-0"
                cols="6"
              >
                <r-sentiment-bar-chart
                  :loading="fetchingDashboardsData"
                  :data="locationsChart"
                  :width="barChartWidth"
                  @click:entity="onClickLocationEntity"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>

      <!-- Reviews Panel -->
      <v-col
        v-show="showReviews"
        ref="reviewscardsection"
        cols="3"
        class="reviewscardsection pa-0"
      >
        <r-review-card-list
          :key="`review-card-list-${showReviews}`"
          :reviews="reviews"
          :total-reviews="numReviews"
          :loading="fetchingReviews"
          :page="page"
          :page-size="pageSize"
          :total-pages="reviewsTotalPage"
          :search="appliedFilters.search"
          :applied-entities="appliedEntityFilters"
          @change:page="onReviewsPageChange"
          @change:sort="onReviewsSortChange"
          @close="() => onShowReviews({ value: false })"
        />
      </v-col>
    </v-row>

    <edit-entity-group-modal
      v-if="showEditEntityMap"
      :entities="$data.selectedWords.map(({ label }) => label)"
      :entity-group-id="editEntityMapGroupId"
      @close="showEditEntityMap = false"
      @submit="onClickSubmitEntityGroupModal"
    />
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import EntityProgress from '@/components/app/dashboards/EntityProgress'
import EntitySettings from '@/components/app/dashboards/EntitySettings.vue'
import KeyColor from '@/components/app/dashboards/KeyColor.vue'
import RAnchoredPopup from '@/components/library/molecules/RAnchoredPopup.vue'
import RBigNumber from '@/components/library/molecules/RBigNumber'
import RButton from '@/components/library/atoms/RButton'
import RComparisonChart from '@/components/library/organisms/RComparisonChart'
import RComponentHeader from '@/components/library/atoms/RComponentHeader'
import RMap from '@/components/library/atoms/RMap'
import RNetSentimentHalfDonut from '@/components/library/molecules/RNetSentimentHalfDonut'
import RRatingTimeLineChart from '@/components/library/atoms/RRatingTimeLineChart'
import RReviewCardList from '@/components/library/molecules/RReviewCardList'
import RSentimentBarChart from '@/components/library/molecules/RSentimentBarChart'
import RSitesAnalyzed from '@/components/library/molecules/RSitesAnalyzed'
import RStarRatingDistributionChart from '@/components/library/molecules/RStarRatingDistributionChart'
import RTimeLineChart from '@/components/library/atoms/RTimeLineChart'
import RTopSentimentReviewCards from '@/components/library/molecules/RTopSentimentReviewCards'
import RTopicWheel from '@/components/library/molecules/RTopicWheel'
import RTrends from '@/components/library/molecules/RTrends'
import RWordCloud from '@/components/library/organisms/RWordCloud'
import RWordCloudGrouped from '@/components/app/dashboards/RWordCloudGrouped.vue'
import TrendingData from '@/components/app/dashboards/TrendingData'
import EditEntityGroupModal from '@/components/app/dashboards/EditEntityGroupModal.vue'
import buildComparisonChartData from '@/utils/buildComparisonChartData'
import { generateColorMap, generateTimeBuckets } from '@/components/app/dashboards/utils'
import { COMPARISON_CHART_DATA_TYPES, TYPES_OF_DASHBOARD } from '@/utils/constants'

const PADDING_X = 112
const STACKED_LABEL_TEXT_WIDTH = 100
export default {
  name: 'SingleProductDashboardContent',
  components: {
    EditEntityGroupModal,
    EntityProgress,
    EntitySettings,
    KeyColor,
    RAnchoredPopup,
    RBigNumber,
    RButton,
    RComparisonChart,
    RComponentHeader,
    RMap,
    RNetSentimentHalfDonut,
    RRatingTimeLineChart,
    RReviewCardList,
    RSentimentBarChart,
    RSitesAnalyzed,
    RStarRatingDistributionChart,
    RTimeLineChart,
    RTopSentimentReviewCards,
    RTopicWheel,
    RTrends,
    RWordCloud,
    RWordCloudGrouped,
    TrendingData,
  },
  props: {
    selectedGroupTab: {
      type: Number,
      default: 0,
    },
    selectedSubTab: {
      type: Number,
      default: 0,
    },
    showReviews: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    page: 1,
    pageSize: 50,
    sort: 'date',
    sortDir: 'desc',
    comparisonSeriesOneValue: 'volume',
    comparisonSeriesOneType: COMPARISON_CHART_DATA_TYPES.AGGREGATE,
    comparisonSeriesTwoValue: 'sentiment',
    comparisonSeriesTwoType: COMPARISON_CHART_DATA_TYPES.BREAKDOWN,
    showDateRangePicker: false,
    startDate: '',
    endDate: '',
    trendsSelectedDays: 7,
    selectedWords: [],
    editEntityMapGroupId: null,
    showEditEntityMap: false,
    entityProgressBarChartWidth: 0,
    barChartWidth: 0,
    appliedSeparateByFilter: 'none',
    appliedHighlightFilter: 'none',
    appliedColorByFilter: 'netSentiment',
    timeBuckets: [],
    timeBucketIntervals: [],
  }),
  computed: {
    ...mapGetters('dashboardsData', [
      'name',
      'numReviews',
      'pinned',
      'starRatingDistribution',
      'sentimentBreakdown',
      'netSentiment',
      'topPositiveReview',
      'topNegativeReview',
      'reviews',
      'sites',
      'reviewsVolumeHistory',
      'reviewsNetRatingHistory',
      'reviewsSentimentHistory',
      'reviewsRatingDistributionHistory',
      'dashboardId',
      'comparisonTrendingData',
      'wordCloudData',
      'groupedWordCloudData',
      'topicWheelData',
      'timeLineChartDurationGap',
      'timeLineChartSeriesData',
      'netRatingHistorySeriesData',
      'dashboardDataStartDate',
      'dashboardDataEndDate',
      'locationsChart',
      'locationsMap',
      'dashboardType',
    ]),
    ...mapGetters('dashboardsFilters', [
      'appliedFilters',
      'appliedEntityFilters',
      'appliedTrendingDataFilters',
      'dateRange',
      'dateRangeAllIsSelected',
    ]),
    ...mapState('dashboardsData', [
      'fetchingDashboardsData',
      'fetchingReviews',
      'entities',
      'trends',
      'fetchingTrends',
      'fetchingDashboardsEntityGroups',
      'fetchingDashboardsEntities',
      'fetchingDashboardsTrendingData',
      'fetchingDashboardsEntitiesWithGroupData',
      'urls',
    ]),
    ...mapState('dashboardsFilters', [
      'search',
    ]),
    ...mapGetters('entityMaps', [
      'entityMapIsApplied',
    ]),
    fetchingWordCloudData() {
      // eslint-disable-next-line max-len
      return this.fetchingDashboardsData || this.fetchingDashboardsEntityGroups || this.fetchingDashboardsEntities
    },
    wordCloudGroupedData() {
      return this.groupedWordCloudData(this.appliedHighlightFilter)
    },
    reviewsTotalPage() {
      return Math.floor(this.numReviews / this.pageSize)
    },
    comparisonChartData() {
      const { data: seriesOneData } = buildComparisonChartData({
        type: this.comparisonSeriesOneType,
        data: this.getComparisonData({ dataType: this.comparisonSeriesOneValue }),
      })
      const { data: seriesTwoData, breakdownKeys } = buildComparisonChartData({
        type: this.comparisonSeriesTwoType,
        data: this.getComparisonData({ dataType: this.comparisonSeriesTwoValue }),
      })

      return {
        data: [
          seriesOneData,
          seriesTwoData,
        ],
        breakdownKeys: breakdownKeys.map(({ labelKey }) => ({
          label: this.$t(`global.keys.${labelKey}`),
          labelKey,
        })),
      }
    },
    showSingleCloudWord() {
      const groupedData = this.groupedWordCloudData()
      const hasSeparateByFilter = !!this.appliedSeparateByFilter && this.appliedSeparateByFilter !== 'none'
      const hasGroupedData = groupedData && Object.keys(groupedData).length

      return !hasSeparateByFilter || !hasGroupedData
    },
    appliedColors() {
      return generateColorMap({
        colorBy: this.appliedColorByFilter,
        timeBucketIntervals: this.timeBucketIntervals,
      })
    },
  },
  watch: {
    async appliedFilters() {
      this.page = 1
      await this.fetchData()
    },
    appliedTrendingDataFilters(newFilters, oldFilters) {
      const { trendingDataDateRangeFilter: newTrendingDates } = newFilters
      const { trendingDataDateRangeFilter: oldTrendingDates } = oldFilters

      const { lastPeriod: newLastPeriod } = newTrendingDates
      const { lastPeriod: oldLastPeriod } = oldTrendingDates
      if (JSON.stringify(newLastPeriod) !== JSON.stringify(oldLastPeriod)) {
        this.fetchComparisonTrendingData({ period: 'lastPeriod' })
      }

      const { thisPeriod: newThisPeriod } = newTrendingDates
      const { thisPeriod: oldThisPeriod } = oldTrendingDates
      if (JSON.stringify(newThisPeriod) !== JSON.stringify(oldThisPeriod)) {
        this.fetchComparisonTrendingData({ period: 'thisPeriod' })
      }
    },
    dateRange() {
      const { minDate, maxDate } = this.appliedFilters
      if (minDate && maxDate) {
        this.startDate = minDate
        this.endDate = maxDate
        this.setTimeBuckets({ minDate, maxDate })
      }
    },
    dashboardDataStartDate() {
      this.loadDashboardTimeBuckets()
    },
  },
  async mounted() {
    if (TYPES_OF_DASHBOARD.SINGLE_PRODUCT === this.dashboardType) {
      this.fetchComparisonChartData({ key: this.comparisonSeriesOneValue })
      this.fetchComparisonChartData({ key: this.comparisonSeriesTwoValue })
      this.fetchWordCloudData()
      this.fetchTopicWheelData()
    }

    this.setEntityProgressBarChartWidth()
    this.setBarchartWidth()
    this.fetchReviews()
    window.addEventListener('resize', this.setDimensions)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setDimensions)
  },
  methods: {
    ...mapActions('dashboardsData', [
      'fetchDashboardsData',
      'fetchDashboardsReviews',
      'fetchDashboardsTrends',
      'fetchDashboardsReviewHistory',
      'fetchDashboardsReviewsNetRatingHistory',
      'fetchDashboardsReviewsSentimentHistory',
      'fetchDashboardsReviewsRatingHistory',
      'fetchTrendingData',
      'fetchDashboardsEntities',
      'fetchDashboardEntityGroups',
      'fetchDashboardsEntitiesWithGroupData',
    ]),
    ...mapActions('dashboardsFilters', [
      'setDashboardFilters',
    ]),
    ...mapActions('entityMaps', [
      'addIgnore',
    ]),
    setTimeBuckets({ minDate, maxDate }) {
      const { timeBuckets, timeBucketIntervals } = generateTimeBuckets({
        minDate,
        maxDate,
        moment: this.$moment,
      })
      this.timeBuckets = timeBuckets
      this.timeBucketIntervals = timeBucketIntervals
    },
    getComparisonData({ dataType }) {
      const COMPARISON_DATA = {
        volume: this.reviewsVolumeHistory,
        ratingAverage: this.reviewsNetRatingHistory,
        sentiment: this.reviewsSentimentHistory,
        ratingDistribution: this.reviewsRatingDistributionHistory,
      }

      return COMPARISON_DATA[dataType] || []
    },
    fetchComparisonChartData({ key }) {
      const COMPARISON_DATA_FETCHER = {
        volume: this.fetchDashboardsReviewHistory,
        ratingAverage: this.fetchDashboardsReviewsNetRatingHistory,
        sentiment: this.fetchDashboardsReviewsSentimentHistory,
        ratingDistribution: this.fetchDashboardsReviewsRatingHistory,
        default: () => {},
      }

      const fetcher = COMPARISON_DATA_FETCHER[key] || COMPARISON_DATA_FETCHER.default
      return fetcher({ dashboardId: this.dashboardId })
    },
    setBarchartWidth() {
      const reviewsWidth = this.$refs.reviewscardsection?.offsetWidth
      this.barChartWidth = this.$props.showReviews
        ? (window.innerWidth - PADDING_X - STACKED_LABEL_TEXT_WIDTH - reviewsWidth) / 2
        : (window.innerWidth - PADDING_X - STACKED_LABEL_TEXT_WIDTH) / 2
    },
    setEntityProgressBarChartWidth() {
      const reviewsWidth = this.$refs.reviewscardsection?.offsetWidth
      this.entityProgressBarChartWidth = this.$props.showReviews
        ? (window.innerWidth - PADDING_X - STACKED_LABEL_TEXT_WIDTH - reviewsWidth)
        : (window.innerWidth - PADDING_X - STACKED_LABEL_TEXT_WIDTH)
    },
    onShowReviews({ value }) {
      this.$emit('on:show-reviews', { value })
      this.setDimensions()
    },
    getPaginationPayload() {
      return {
        page: this.page,
        pageSize: this.pageSize,
      }
    },
    getSortingPayload() {
      return {
        sort: this.sort,
        sortDir: this.sortDir,
      }
    },
    fetchReviews() {
      const pagination = this.getPaginationPayload()
      const sorting = this.getSortingPayload()
      this.fetchDashboardsReviews({
        dashboardId: this.dashboardId,
        pagination,
        sorting,
      })
    },
    fetchComparisonTrendingData({ period }) {
      this.fetchTrendingData({
        dashboardId: this.dashboardId,
        period,
      })
    },
    onChangeComparisonSeriesOne({ value }) {
      const { key, type } = value

      this.comparisonSeriesOneValue = key
      this.comparisonSeriesOneType = type

      this.fetchComparisonChartData({ key })
    },
    onChangeComparisonSeriesTwo({ value }) {
      const { key, type } = value

      this.comparisonSeriesTwoValue = key
      this.comparisonSeriesTwoType = type

      this.fetchComparisonChartData({ key })
    },
    loadDashboardTimeBuckets() {
      if (this.dateRangeAllIsSelected && this.dashboardDataStartDate) {
        this.setTimeBuckets({
          minDate: this.dashboardDataStartDate,
          maxDate: this.dashboardDataEndDate,
        })
      }
    },
    async onReviewsPageChange({ value }) {
      this.page = value
      this.fetchReviews()
    },
    async onReviewsSortChange({ value }) {
      const { sort, sortDir } = value
      this.sort = sort
      this.sortDir = sortDir
      this.fetchReviews()
    },
    async handleTrendsDateChange({ value }) {
      this.trendsSelectedDays = value
      const maxDate = this.$moment().format('YYYY-MM-DD')
      const minDate = this.$moment().subtract(value - 1, 'days').format('YYYY-MM-DD')
      this.fetchDashboardsTrends({
        dashboardId: this.dashboardId,
        minDate,
        maxDate,
      })
    },
    async fetchData() {
      this.fetchReviews()
      this.fetchComparisonChartData({ key: this.comparisonSeriesOneValue })
      this.fetchComparisonChartData({ key: this.comparisonSeriesTwoValue })
      this.fetchWordCloudData()
      this.fetchTopicWheelData()

      await this.fetchDashboardsData({ dashboardId: this.dashboardId })
      this.loadDashboardTimeBuckets()
    },
    async onChangeTrendingPeriodDate({ value }) {
      const { dateRange, period } = value

      this.setDashboardFilters({
        trendingDataDateRangeFilter: {
          [period]: { ...dateRange },
        },
      })
    },
    onClickSentiment({ value }) {
      this.setDashboardFilters({ sentiment: value })
    },
    onClickMapCountry({ value }) {
      const { clickedCountry } = value
      this.setDashboardFilters({ countries: [clickedCountry] })
    },
    onClickRating({ value }) {
      this.setDashboardFilters({ ratings: [value] })
    },
    onClickComparisonChartSeries({ value }) {
      const {
        clickedSeries,
        clickedValue,
        dateFrom: startDate,
        dateTo: endDate,
      } = value

      let filterPayload = { dateRange: [startDate, endDate] }

      if (clickedSeries === 'ratingDistribution') {
        const rating = parseInt(clickedValue, 10)
        filterPayload = {
          ...filterPayload,
          ratings: [rating],
        }
      }

      if (clickedSeries === 'sentiment') {
        filterPayload = {
          ...filterPayload,
          sentiment: clickedValue.toUpperCase(),
        }
      }

      this.setDashboardFilters(filterPayload)
    },
    onClickTimeLineChartPoint({ value }) {
      this.setDashboardFilters({ dateRange: value })
    },
    onClickHostname({ value }) {
      this.setDashboardFilters({ hostnames: [value] })
    },
    onClickLocationEntity({ value: { label, entitySentiment } }) {
      this.setDashboardFilters({ countries: [label], sentiment: entitySentiment })
    },
    onClickEntitySentiment({ value }) {
      this.setDashboardFilters({ entitySentiment: value })
    },
    onClickEntity({ value }) {
      const { label, entitySentiment, entityGroupId } = value
      if (entityGroupId) {
        this.setDashboardFilters({ entityGroupId, entitySentiment })
      } else {
        this.setDashboardFilters({ entity: label, entitySentiment })
      }
    },
    onClickEntityName({ value }) {
      const { label, entityGroupId } = value

      if (entityGroupId) {
        this.setDashboardFilters({ entityGroupId })
      } else {
        this.setDashboardFilters({ entity: label })
      }
    },
    onChangeSelectedWords({ value: newSelectedWords }) {
      this.$data.selectedWords = newSelectedWords
    },
    onClickEditEntityMapGroup({ value }) {
      this.$data.editEntityMapGroupId = value
      this.$data.showEditEntityMap = true
    },
    onClickAddToEntityMap() {
      this.$data.editEntityMapGroupId = null
      this.$data.showEditEntityMap = true
    },
    async fetchWordCloudData() {
      if (this.showSingleCloudWord) {
        return this.fetchDashboardsEntities()
      }

      return this.fetchDashboardEntityGroups({
        separateBy: this.appliedSeparateByFilter,
        urls: this.urls,
        timeBuckets: this.timeBuckets,
      })
    },
    fetchTopicWheelData() {
      return this.fetchDashboardsEntitiesWithGroupData()
    },
    async onClickIgnore() {
      const ignore = this.$data.selectedWords.map(({ label }) => label)
      this.$data.selectedWords = []
      await this.addIgnore({ ignore })
      this.fetchWordCloudData()
      return this.fetchTopicWheelData()
    },
    onClickWord({ value }) {
      const { label, entityGroupId } = value
      if (entityGroupId) {
        this.setDashboardFilters({ entityGroupId })
      } else {
        this.setDashboardFilters({ entity: label })
      }
    },
    async setDimensions() {
      await this.$nextTick()
      this.setBarchartWidth()
      this.setEntityProgressBarChartWidth()
    },
    onClickSubmitEntityGroupModal() {
      this.fetchWordCloudData()
      this.fetchTopicWheelData()
      this.$data.selectedWords = []
    },
    async onApplyEntitySettingsFilter({ separateBy, highlight, colorBy }) {
      if (separateBy === 'none') {
        this.wordCloudGrouped = {}
        this.appliedSeparateByFilter = 'none'
        this.appliedHighlightFilter = 'none'
        return
      }
      if (separateBy && this.appliedSeparateByFilter !== separateBy) {
        this.appliedSeparateByFilter = separateBy
        await this.fetchDashboardEntityGroups({
          separateBy: this.appliedSeparateByFilter,
          urls: this.urls,
          timeBuckets: this.timeBuckets,
        })
      }

      if (highlight && this.appliedHighlightFilter !== highlight) {
        this.appliedHighlightFilter = highlight
      }

      if (colorBy && this.appliedColorByFilter !== colorBy) {
        this.appliedColorByFilter = colorBy
      }
    },
  },
}
</script>
<style scoped>
.dashboardcontent {
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}
.singleproductdashboard--tabs {
  margin-top: 16px;
  margin-bottom: 22px;
  border-bottom: 1px solid var(--r-border-color);
}
.singleproductdashboard--filter-row {
  margin-top: 20px;
  margin-bottom: 20px;
}
.singleproductdashboard--summary--box {
  border: 1px solid var(--r-border-color);
  height: 702px;
  overflow: hidden;
  border-radius: 3px;
}
.singleproductdashboard--sites {
  margin-top: 82px;
  margin-bottom: 44px;
}
.rcomponentheader--bordered {
  border-bottom: 1px solid var(--r-border-color);
  padding-bottom: 8px;
  margin-bottom: 8px;
}
/deep/ .rpageheader {
  border-bottom: 0;
}
/deep/ .v-skeleton-loader__image {
  height: 1000px;
}
/deep/ .singleproductdashboard--tabs .v-skeleton-loader__button  {
  height: 28px;
}
/deep/ .singleproductdashboard--filter-row .v-skeleton-loader__button,
/deep/ .singleproductdashboard--filter-row .v-skeleton-loader__chip {
  height: 24px;
}
.reviewscardsection {
  background-color: white;
  width: 25%;
}
.singleproductdashboard--date-range > div {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: max-content;
}
.singleproductdashboard--date-range i {
  font-weight: 700;
  margin-left: 10px;
}
.position-relative {
  position: relative;
}
.singleproductdashboard--trendingfilters {
  background-color: var(--soft-grey-color);
}
.entitysettingspopup {
  z-index: 1;
}
.wordcloudheaderbutton {
  margin-top: -1rem;
}
</style>
